import { Platform } from '../enums/device-status.enum.js';
import { FACEBOOK_ACTIVITY, FACEBOOK_PACKAGE, INSTAGRAM_ACTIVITY, INSTAGRAM_PACKAGE, TIKTOK_ACTIVITY, TIKTOK_PACKAGE, TIKTOK_REGIONAL_PACKAGE, TWITTER_ACTIVITY, TWITTER_PACKAGE, YOUTUBE_ACTIVITY, YOUTUBE_PACKAGE, } from './device.constants.js';
/** Human-readable platform names for the dashboard and operator-facing messages. */
export const PLATFORM_DISPLAY_NAMES = {
    [Platform.INSTAGRAM]: 'Instagram',
    [Platform.TIKTOK]: 'TikTok',
    [Platform.YOUTUBE]: 'YouTube',
    [Platform.TWITTER]: 'X (Twitter)',
    [Platform.FACEBOOK]: 'Facebook',
    [Platform.ONLYFANS]: 'OnlyFans',
};
/** Display name for `platform`; an unknown value is shown as-is. */
export function platformDisplayName(platform) {
    return PLATFORM_DISPLAY_NAMES[platform] ?? platform;
}
/** OnlyFans has no app in Google Play; creators use the mobile site. */
export const ONLYFANS_WEB_URL = 'https://onlyfans.com';
/**
 * Every Android build of a platform's app the platform drives, in preference
 * order; empty when the platform ships no Android app. A phone runs whichever
 * build its region offers (the farm vivo has TikTok's regional
 * `com.ss.android.ugc.trill`, not the global `com.zhiliaoapp.musically`), so
 * device-native flows resolve the build installed on the phone
 * (`resolveInstalledAppTarget`) instead of assuming the first.
 *
 * Only builds whose screens the automation reads are listed. Facebook Lite
 * (`com.facebook.lite`, also on the farm vivo) is not: it draws its UI itself,
 * and nothing shows its screens are readable through uiautomator.
 *
 * Callers must never substitute another platform's app: launching,
 * inspecting or wiping the wrong app is a cross-account risk.
 */
export const PLATFORM_APP_VARIANTS = {
    [Platform.INSTAGRAM]: [{ pkg: INSTAGRAM_PACKAGE, activity: INSTAGRAM_ACTIVITY }],
    [Platform.TIKTOK]: [
        { pkg: TIKTOK_PACKAGE, activity: TIKTOK_ACTIVITY },
        { pkg: TIKTOK_REGIONAL_PACKAGE, activity: TIKTOK_ACTIVITY },
    ],
    [Platform.YOUTUBE]: [{ pkg: YOUTUBE_PACKAGE, activity: YOUTUBE_ACTIVITY }],
    [Platform.TWITTER]: [{ pkg: TWITTER_PACKAGE, activity: TWITTER_ACTIVITY }],
    [Platform.FACEBOOK]: [{ pkg: FACEBOOK_PACKAGE, activity: FACEBOOK_ACTIVITY }],
    [Platform.ONLYFANS]: [],
};
/** Every app build of `platform`, in preference order: empty for an unknown platform or one without an Android app. */
export function platformAppTargets(platform) {
    return PLATFORM_APP_VARIANTS[platform] ?? [];
}
/** Package names of every app build of `platform`. */
export function platformPackages(platform) {
    return platformAppTargets(platform).map((t) => t.pkg);
}
/** True when `pkg` is one of `platform`'s app builds. */
export function isPlatformPackage(platform, pkg) {
    return platformPackages(platform).includes(pkg);
}
/**
 * The preferred (first) app build per platform, or null when the platform
 * ships no Android app. For defaults and display only: device actions use the
 * build installed on the phone (`resolveInstalledAppTarget`).
 */
export const PLATFORM_APP_TARGETS = Object.freeze(Object.fromEntries(Object.keys(PLATFORM_APP_VARIANTS).map((p) => [p, PLATFORM_APP_VARIANTS[p][0] ?? null])));
/** Preferred app build for `platform`: null for an unknown platform or one without an Android app. */
export function platformAppTarget(platform) {
    return platformAppTargets(platform)[0] ?? null;
}
/**
 * The first of `platform`'s app builds that `isInstalled` reports on the
 * phone, in preference order, or null when none is. A failing check counts
 * as not installed for that build only.
 */
export async function resolveInstalledAppTarget(platform, isInstalled) {
    for (const target of platformAppTargets(platform)) {
        let installed = false;
        try {
            installed = await isInstalled(target.pkg);
        }
        catch {
            installed = false;
        }
        if (installed)
            return target;
    }
    return null;
}
/**
 * Platforms whose logged-in account can be identified from an authoritative
 * source (backend/src/services/social-identity). Only these can be connected;
 * the others are refused before any device action. The backend provider
 * registry is the enforcement point, and a unit test keeps this list in step
 * with it. See docs/security/social-account-identity.md.
 */
export const IDENTITY_SUPPORTED_PLATFORMS = [Platform.INSTAGRAM];
export function isIdentitySupportedPlatform(platform) {
    return IDENTITY_SUPPORTED_PLATFORMS.includes(platform);
}
/**
 * Platforms with a publish flow proven on a real phone
 * (backend automation.service publish-flow registry, kept in step by a unit
 * test). Scheduling refuses every other platform up front, a queued job is
 * blocked before it touches the phone, and automation refuses before the app
 * is launched. A platform joins only with real-device evidence of its whole
 * flow: docs/accounts/adding-a-platform.md.
 */
export const PUBLISHING_SUPPORTED_PLATFORMS = [Platform.INSTAGRAM];
export function isPublishingSupportedPlatform(platform) {
    return PUBLISHING_SUPPORTED_PLATFORMS.includes(platform);
}
export function platformCapabilities(platform) {
    return {
        androidApp: platformAppTargets(platform).length > 0,
        connect: isIdentitySupportedPlatform(platform),
        publish: isPublishingSupportedPlatform(platform),
    };
}
//# sourceMappingURL=platform.constants.js.map